import { usePermissionsStore } from "@/stores/permissions-store";
import { grantsField } from "@/lib/field-path-matcher";

export interface EntityPermissions {
  has_create: boolean;
  has_delete: boolean;
  has_read: boolean;
  has_update: boolean;
}

// A single permission row that applies to an entity (merged across ACO ancestors), kept un-collapsed
// so its `field_paths` whitelist stays paired with the operations it grants. Used for per-field
// read/write gating in forms.
export interface FieldPermissionRow {
  has_create: boolean;
  has_read: boolean;
  has_update: boolean;
  // Parsed `field_paths` whitelist for this row; always at least `["*"]` (full access).
  field_paths: string[];
  // Raw row-level conditions; empty (see `isEmptyConditions`) means the row applies to every record.
  conditions_on_current?: string;
  conditions_on_patched?: string;
}

const defaultPermissions = {
  has_create: false,
  has_delete: false,
  has_read: false,
  has_update: false,
};

export const usePermissionForEntityId = (entityId: string) => {
  const entityPermissionsMap = usePermissionsStore((state) => state.entityPermissionsMap);
  const permission = entityPermissionsMap?.get(entityId);
  return permission ?? defaultPermissions;
};

export const useFieldPermissionsForEntityId = (entityId: string) => {
  const entityFieldPermissionsMap = usePermissionsStore((state) => state.entityFieldPermissionsMap);
  return entityFieldPermissionsMap?.get(entityId);
};

// Per-field access for an entity, mirroring the backend `PyloPermissionService::isFieldReadable`
// for the own-entity case (relationPath = []). A field is granted when any applicable row (one that
// grants the operation) whitelists it via its `field_paths`. Returns true when access can't be
// determined (no rows) so callers don't lock fields before permissions resolve or for unrestricted
// setups — entity-level gating already covers those cases.
export const isFieldGranted = (
  rows: FieldPermissionRow[] | undefined,
  op: "read" | "create" | "update",
  field: string,
): boolean => {
  if (!rows || rows.length === 0) return true;
  const applicable = rows.filter((row) =>
    op === "read" ? row.has_read : op === "create" ? row.has_create : row.has_update,
  );
  if (applicable.length === 0) return false;
  return applicable.some((row) => grantsField(row.field_paths, [], field));
};

export const useEntityPermissionsMap = () => {
  const entityPermissionsMap = usePermissionsStore((state) => state.entityPermissionsMap);
  const hasLoaded = usePermissionsStore((state) => state.hasLoaded);

  return {
    entityPermissionsMap,
    hasLoaded,
  };
};
